import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CaseIdSchema, SourceSchema, type Source } from '@casetrace/shared';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

function toSources(caseId: string, docs: Array<{ id: string; data: () => unknown }>): Source[] {
  return docs.map((doc) => {
    const rawData = doc.data();
    return SourceSchema.parse({ ...(rawData as Record<string, unknown>), id: doc.id, caseId });
  });
}

async function fetchSources(caseId: string): Promise<Source[]> {
  const parsedCaseId = CaseIdSchema.parse(caseId);
  const ref = collection(db, 'cases', parsedCaseId, 'sources');
  const snap = await getDocs(ref);
  return toSources(parsedCaseId, snap.docs);
}

export function useCaseSources(caseId: string) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['case-sources', caseId] as const,
    queryFn: () => fetchSources(caseId),
    enabled: Boolean(caseId),
  });

  useEffect(() => {
    if (!caseId) return;
    const parsedCaseId = CaseIdSchema.parse(caseId);
    const ref = collection(db, 'cases', parsedCaseId, 'sources');
    return onSnapshot(ref, (snap) => {
      queryClient.setQueryData(['case-sources', caseId], toSources(parsedCaseId, snap.docs));
    });
  }, [caseId, queryClient]);

  return query;
}
